/**
 * DNS zones we keep for a domain (subsystems/dns/facade/adapters/admin-api/src/admin/dns.rs): the
 * zone, its records with their owner, its versions and what was sent to the DNS server; staff edit
 * the customer's records with a reason (each change is a new version and one `apply` queued for
 * provisioning-worker). The rules are the DNS core's (subsystems/dns/core/src/validate.rs).
 */

import type { CallOptions, ISODateTime, WriteOptions } from '../types';

/** The record types the DNS branch keeps (`RecordType::ALL`). */
export const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA', 'NS', 'PTR', 'TLSA'] as const;
export type RecordType = (typeof RECORD_TYPES)[number];

/** Why a version was made (`dns.zone_versions.reason`). */
export type ZoneVersionReason = 'CREATE' | 'EDIT' | 'IMPORT' | 'TEMPLATE' | 'ROLLBACK' | 'PUBLISH' | 'WITHDRAW';

export interface DnsZone {
  service_id: string;
  apex: string;
  customer_id: string;
  /** The zone's service (`provisioning.services.state`). */
  state: string;
  module: string;
  instance_key: string;
  kind: 'PRIMARY' | 'SECONDARY';
  /** The provider's name servers, once it answered them. */
  nameservers: string[];
  /** The version our records are at. */
  desired_version: number;
  /** The version the DNS server holds (0: none sent yet). */
  applied_version: number;
  dnssec: 'OFF' | 'ENABLING' | 'ON' | 'DISABLING';
  platform: boolean;
  /** What the provider held differently at the last check (null: the same, or not checked). */
  drift: unknown;
  drift_checked_at: ISODateTime | null;
}

/** One record; `owner` null is the customer's, else the product that published it (`email:<service id>`). */
export interface DnsRecord {
  data: string;
  owner: string | null;
}

export interface DnsRecordSet {
  /** Relative to the apex: `@`, `www`, `_dmarc`. */
  name: string;
  type: RecordType;
  ttl: number;
  records: DnsRecord[];
}

export interface DnsZoneVersion {
  version: number;
  reason: ZoneVersionReason;
  note: string | null;
  actor_id: string;
  /** The staff member's name (null for a worker or a customer). */
  actor_name: string | null;
  /** The reason staff gave for the change (its audit row; null when no staff member made it). */
  staff_reason: string | null;
  created_at: ISODateTime;
}

export interface DnsOperation {
  id: string;
  op: string;
  status: 'QUEUED' | 'SENDING' | 'RUNNING' | 'SUCCEEDED' | 'REFUSED' | 'FAILED' | 'UNKNOWN' | 'CANCELLED';
  attempts: number;
  max_attempts: number;
  last_outcome: string | null;
  refusal: { code: string; provider_code: string | null; message: string | null } | null;
  failure: { class: string; detail: string } | null;
  attention: string | null;
  next_attempt_at: ISODateTime;
  sent_at: ISODateTime | null;
  finished_at: ISODateTime | null;
  created_at: ISODateTime;
}

/**
 * The domain's DNS login (owner 2026-10-07): a partner's customer manages the zone with the domain
 * name and this password, without an account. Never the password.
 */
export interface DnsLogin {
  set_by_type: 'OPERATOR' | 'CUSTOMER' | 'SYSTEM' | 'EXTERNAL';
  /** The staff member's name when staff set it. */
  set_by_name: string | null;
  set_at: ISODateTime;
  /** Sessions open now. */
  open_sessions: number;
}

export interface DnsZoneDetail {
  zone: DnsZone;
  /** null: the zone has no DNS login. */
  login: DnsLogin | null;
  records: DnsRecordSet[];
  /** Newest first, at most 20. */
  versions: DnsZoneVersion[];
  /** Newest first, at most 10. */
  operations: DnsOperation[];
}

export type DnsChange =
  | { action: 'upsert'; name: string; type: RecordType; ttl: number; records: string[] }
  | { action: 'delete'; name: string; type: RecordType };

/** A change of the desired records: the new version, and the `apply` queued (null: nothing changed). */
export interface DnsEdit {
  version: number;
  ticket: { operation_id: string; status: string; created: boolean } | null;
}

/** One rule a record set breaks (details of `ZONE_RECORDS_INVALID`). */
export interface DnsViolation {
  name: string;
  type: RecordType | null;
  code: string;
  rule: string;
  message: string;
}

export type ImportMode = 'replace' | 'merge';

export interface DnsTemplate {
  key: string;
  name: string;
  description: string | null;
  records: { name: string; type: RecordType; ttl: number | null; data: string }[];
  /** The values it asks for besides `domain`. */
  variables: string[];
  builtin: boolean;
}

export interface DnsImported {
  zone_id: string;
  edit: DnsEdit;
  skipped: { line: number; owner: string; rtype: string; reason: string }[];
  warnings: string[];
}

export interface DnsApi {
  /** The zone we keep for the domain's customer (`null`: none). */
  findDnsZone(domain: string, customerId: string, opts?: CallOptions): Promise<{ zone: DnsZone | null }>;
  getDnsZone(zoneId: string, opts?: CallOptions): Promise<DnsZoneDetail>;
  getDnsZoneVersion(zoneId: string, version: number, opts?: CallOptions): Promise<{ zone_id: string; version: number; records: DnsRecordSet[] }>;
  editDnsRecords(zoneId: string, body: { changes: DnsChange[]; reason: string }, opts?: WriteOptions): Promise<{ zone_id: string; edit: DnsEdit }>;
  rollbackDnsZone(zoneId: string, body: { version: number; reason: string }, opts?: WriteOptions): Promise<{ zone_id: string; edit: DnsEdit }>;
  exportDnsZone(zoneId: string, opts?: CallOptions): Promise<{ zone_id: string; text: string }>;
  importDnsZone(zoneId: string, body: { text: string; mode: ImportMode; reason: string }, opts?: WriteOptions): Promise<DnsImported>;
  listDnsTemplates(opts?: CallOptions): Promise<{ templates: DnsTemplate[] }>;
  applyDnsTemplate(zoneId: string, body: { key: string; vars: Record<string, string>; mode: ImportMode; reason: string }, opts?: WriteOptions): Promise<{ zone_id: string; edit: DnsEdit }>;
  /** Sets or replaces the zone's DNS password (12–128 characters); its open sessions end. */
  setDnsLogin(zoneId: string, body: { password: string; reason: string }, opts?: WriteOptions): Promise<{ zone_id: string; login: unknown }>;
  /** Removes the zone's DNS login; its open sessions end. */
  revokeDnsLogin(zoneId: string, body: { reason: string }, opts?: WriteOptions): Promise<{ zone_id: string; revoked: boolean }>;
}
